import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withStyles, IconButton, withTheme } from '@material-ui/core';
import { AddCircle } from '@material-ui/icons';
import moment from 'moment';
import CanvasJSReact from '../../../components/LookerDashboardGraphs/CanvasJS';

const { CanvasJSChart } = CanvasJSReact;

const styles = () => ({
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  chartWrapper: {
    width: '100%',
    height: '100%',
    '& > div': {
      maxHeight: '100% !important',
    },
  },
  fullWidth: {
    width: '100%',
  },
  expandButton: {
    right: '10px',
    bottom: '40px',
    position: 'absolute',
    zIndex: 10,
  },
  noDataMessage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    color: '#666',
    fontSize: '14px',
    fontFamily: 'Calibri, Arial, sans-serif',
  },
});

/**
 * Get a color for a line based on its index
 */
const getLineColor = index => {
  const colors = [
    '#2196F3',
    '#4CAF50',
    '#FF9800',
    '#E91E63',
    '#9C27B0',
    '#00BCD4',
    '#FF5722',
    '#3F51B5',
    '#009688',
    '#FFC107',
  ];
  return colors[index % colors.length];
};

/**
 * Transform graph data from API format to CanvasJS format
 */
const transformDataToCanvasJS = graphData => {
  if (!graphData || !graphData.dates || !graphData.lines) {
    return [];
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return graphData.lines.map((line, index) => {
    const dataPoints = line.values
      .map((value, i) => ({
        x: new Date(graphData.dates[i]),
        y: value,
      }))
      .filter(point => point.y !== null && !Number.isNaN(point.y));

    if (dataPoints.length > 0) {
      const lastPoint = dataPoints[dataPoints.length - 1];
      const lastDate = new Date(lastPoint.x);
      lastDate.setHours(0, 0, 0, 0);

      if (lastDate < today) {
        dataPoints.push({
          x: new Date(),
          y: lastPoint.y,
        });
      }
    }

    return {
      type: 'line',
      name: line.label,
      showInLegend: true,
      markerSize: 0,
      toolTipContent: `Date: {x}<br/>${line.label}<br/>Qty: {y}`,
      xValueFormatString: 'MM/DD/YY hh:mm tt',
      dataPoints,
      color: getLineColor(index),
    };
  });
};

const SingleInteractiveGraph = props => {
  const { classes, theme, graphData } = props;
  const chartRef = useRef(null);

  const [chartData, setChartData] = useState([]);
  const [legendsVisible, setLegendsVisible] = useState(true);

  // Update chart data when graphData changes
  useEffect(() => {
    if (graphData) {
      const data = transformDataToCanvasJS(graphData);
      setChartData(data);
    }
  }, [graphData]);

  const toggleDataSeries = e => {
    const seriesIndex = e.dataSeriesIndex;
    setChartData(prev =>
      prev.map((series, idx) => {
        if (idx === seriesIndex) {
          return {
            ...series,
            visible: !(typeof series.visible === 'undefined' || series.visible),
          };
        }
        return series;
      })
    );
  };

  const toggleAllLegends = () => {
    const newVisibility = !legendsVisible;
    setChartData(prev =>
      prev.map(series => ({
        ...series,
        showInLegend: newVisibility,
      }))
    );
    setLegendsVisible(newVisibility);
  };

  const showExpand = chartData.length > 5;

  const formattedEventDate = graphData?.dates?.length
    ? moment(new Date(Math.max(...graphData.dates.map(d => new Date(d))))).format('YYYY-MM-DD hh:mm:ss A')
    : '';

  // Build options object for rendering
  const options = {
    animationEnabled: true,
    zoomEnabled: true,
    zoomType: 'xy',
    theme: theme.palette.type === 'dark' ? 'dark2' : 'light2',
    title: {
      text: `Held Over Time By Cost [${formattedEventDate}]`,
      fontSize: 14,
      fontFamily: 'Calibri, Arial, sans-serif',
      fontWeight: 'normal',
    },
    axisX: {
      valueFormatString: 'MM-DD-YYYY',
      labelAngle: -45,
      maximum: graphData?.event_date ? new Date(graphData.event_date) : undefined,
    },
    axisY: {
      gridThickness: 1,
    },
    legend: {
      cursor: 'pointer',
      fontSize: 10,
      verticalAlign: 'bottom',
      horizontalAlign: 'center',
      itemclick: toggleDataSeries,
    },
    data: chartData,
  };

  if (!graphData || !graphData.lines || graphData.lines.length === 0) {
    return (
      <div className={classes.container}>
        <div className={classes.noDataMessage}>Held over time by cost graph not available</div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.chartWrapper}>
        <CanvasJSChart
          options={options}
          onRef={ref => {
            chartRef.current = ref;
          }}
        />
      </div>
      {legendsVisible && showExpand && (
        <IconButton onClick={toggleAllLegends} className={classes.expandButton}>
          <AddCircle />
        </IconButton>
      )}
    </div>
  );
};

SingleInteractiveGraph.propTypes = {
  theme: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  graphData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string).isRequired,
    lines: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        values: PropTypes.arrayOf(PropTypes.number).isRequired,
      })
    ).isRequired,
    event_date: PropTypes.string,
  }),
};

SingleInteractiveGraph.defaultProps = {
  graphData: null,
};

export default withStyles(styles)(withTheme(SingleInteractiveGraph));
